// AddDesignModal — popup that opens when a customer clicks
// "Add Design" in the left nav (Designer) or the "Add artwork" wizard
// step. Two tabs:
//
//   Upload         File-picker + drag-drop zone — bound to the
//                  Designer's existing onPickFile.
//   Generate (AI)  Prompt textarea → POST /api/files/generate-design
//                  → host's onUseGeneratedFile(file) gets the saved
//                  file row so the canvas can drop it via useUpload().
//
// Styling matches the brand brick-on-cream WizardStepModal so the
// flow reads as "still part of the wizard" even when triggered from
// the left-nav button.
import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'
import { useAuth } from '@workos-inc/authkit-react'

export interface GeneratedFile {
  id: string
  url: string
  originalName: string
  mimeType: string
  size: number
  widthPx: number | null
  heightPx: number | null
}

export default function AddDesignModal({
  open, onClose,
  onPickFile,
  onUseGeneratedFile,
  garmentName,
  garmentColourHex,
  garmentColourName,
}: {
  open: boolean
  onClose: () => void
  /** Same handler that the inline file picker uses — keeps the upload
   *  pipeline (preview, consent modal, library row) identical. */
  onPickFile: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Fires after a successful AI generation. Host drops the file on
   *  the canvas (typically by calling its existing useUpload()). */
  onUseGeneratedFile: (file: GeneratedFile) => void
  /** Context for the AI prompt — informs colour-contrast choice. */
  garmentName?: string | null
  garmentColourHex?: string | null
  garmentColourName?: string | null
}) {
  const { getAccessToken } = useAuth()
  const [tab, setTab] = useState<'upload' | 'ai'>('upload')
  const [dragging, setDragging] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [genState, setGenState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [genError, setGenError] = useState<string | null>(null)
  const hiddenFileRef = useRef<HTMLInputElement | null>(null)

  // ── AI-tab extras: optional reference image + pinned brand palette ──
  // Reference image is converted to a data URL and sent to the API
  // as-is; the backend forwards it to fal-ai/nano-banana-2/edit and
  // tells the model to use it as a STYLE / COMPOSITION reference, not
  // a copy target. Primary / Secondary colours are explicit overrides:
  // when set they win over any colour the customer types in the brief.
  const [refDataUrl, setRefDataUrl] = useState<string | null>(null)
  const [refName, setRefName]       = useState<string | null>(null)
  const [primaryHex, setPrimaryHex]     = useState<string | null>(null)
  const [secondaryHex, setSecondaryHex] = useState<string | null>(null)
  const refInputRef = useRef<HTMLInputElement | null>(null)

  // ── Progress bar ──────────────────────────────────────────────────
  // The FAL chain (Nano Banana 2 → BiRefNet v2) takes ~25–35 s in
  // practice and exposes no progress events on the synchronous
  // endpoint. Drive a smooth time-based fake progress so the customer
  // sees movement instead of an indeterminate spinner. Snaps to 100 %
  // on success / error so the closing animation looks finished.
  //   0–25 s  Generating logo            0 → 80 %
  //   25–30 s Removing background        80 → 92 %
  //   >30 s   Final touches              92 → 95 % (asymptotic)
  const [genProgress, setGenProgress] = useState(0)
  const [genStage,    setGenStage]    = useState<string>('Generating logo…')
  const genStartRef = useRef<number>(0)
  useEffect(() => {
    if (genState !== 'busy') return
    genStartRef.current = Date.now()
    setGenProgress(0); setGenStage('Generating logo…')
    const id = window.setInterval(() => {
      const sec = (Date.now() - genStartRef.current) / 1000
      let pct: number; let stage: string
      if (sec < 25) {
        pct = (sec / 25) * 80
        stage = 'Generating logo…'
      } else if (sec < 30) {
        pct = 80 + ((sec - 25) / 5) * 12
        stage = 'Removing background…'
      } else {
        // Asymptotic approach to 95 % so the bar never stalls visibly.
        pct = 92 + Math.min(3, (sec - 30) * 0.15)
        stage = 'Final touches…'
      }
      setGenProgress(Math.min(95, pct))
      setGenStage(stage)
    }, 200)
    return () => window.clearInterval(id)
  }, [genState])

  // ESC closes — but ignore while we're mid-generation so a stray key
  // doesn't abandon a paid call.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && genState !== 'busy') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, genState])

  // Reset transient state when the modal is closed so the next open
  // doesn't reuse a stale error or busy spinner.
  useEffect(() => {
    if (!open) {
      setGenState('idle'); setGenError(null); setDragging(false)
      setRefDataUrl(null); setRefName(null)
      setPrimaryHex(null); setSecondaryHex(null)
    }
  }, [open])

  // Read a picked reference image as a data URL so we can ship it
  // straight to the API without a separate upload round-trip.
  function pickReference(file: File) {
    if (!file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = () => {
      const r = reader.result
      if (typeof r === 'string') { setRefDataUrl(r); setRefName(file.name) }
    }
    reader.readAsDataURL(file)
  }

  if (!open) return null

  // ── Upload tab — drag-drop forwards into the existing onPickFile
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    // Forward via the hidden input so onPickFile's existing handler
    // (preview probe, consent modal, library entry) runs unchanged.
    if (hiddenFileRef.current) {
      const dt = new DataTransfer()
      dt.items.add(file)
      hiddenFileRef.current.files = dt.files
      const evt = new Event('change', { bubbles: true })
      hiddenFileRef.current.dispatchEvent(evt)
    }
    onClose()
  }

  // ── AI tab — POST the prompt, wait, drop the result on the canvas
  async function generate() {
    if (!prompt.trim()) return
    setGenState('busy'); setGenError(null)
    try {
      const f = await apiFetch<GeneratedFile>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/files/generate-design',
        {
          method: 'POST',
          body: JSON.stringify({
            prompt: prompt.trim(),
            garmentColourHex:  garmentColourHex  || undefined,
            garmentColourName: garmentColourName || undefined,
            garmentName:       garmentName       || undefined,
            referenceImageDataUrl: refDataUrl || undefined,
            primaryColourHex:      primaryHex   || undefined,
            secondaryColourHex:    secondaryHex || undefined,
          }),
        },
      )
      // Snap the progress bar to 100 % for a beat so the bar reads as
      // "completed" instead of "interrupted" before the modal closes.
      setGenProgress(100); setGenStage('Ready')
      onUseGeneratedFile(f)
      window.setTimeout(() => {
        setGenState('idle'); setPrompt(''); onClose()
      }, 350)
    } catch (e) {
      // AI generation now requires sign-in. On a 401, send the user through the
      // server sign-in flow (same path as Login.tsx) and stash the current
      // designer URL so Home.tsx routes them straight back after auth.
      if ((e as Error & { status?: number }).status === 401) {
        try { sessionStorage.setItem('evrst-prints:returnTo', window.location.pathname + window.location.search) } catch { /* private mode */ }
        setGenState('error'); setGenError('Please sign in to generate AI designs — redirecting…')
        window.location.assign('/auth/login')
        return
      }
      setGenState('error'); setGenError((e as Error).message || 'Generation failed.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && genState !== 'busy') onClose() }}>
      <div className="bg-cream rounded-lg shadow-2xl max-w-2xl w-full max-h-[88vh] flex flex-col overflow-hidden border border-brick/40">

        {/* Header — brick band, matches WizardStepModal */}
        <div className="bg-brick text-white px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">Step 3 · Add artwork</div>
            <div className="text-sm font-semibold">How would you like to add your design?</div>
          </div>
          <button onClick={onClose} disabled={genState === 'busy'}
            className="w-8 h-8 rounded hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Tab strip */}
        <div className="px-5 pt-4 border-b border-brick/20 bg-cream shrink-0 flex gap-1">
          <TabBtn active={tab === 'upload'} onClick={() => setTab('upload')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Upload a file
          </TabBtn>
          <TabBtn active={tab === 'ai'} onClick={() => setTab('ai')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2 7h7l-5.5 4.5L17 21l-5-4-5 4 1.5-7.5L3 9h7l2-7z"/>
            </svg>
            Generate with AI
            <span className="ml-1 inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brick-tint text-brick-dark border border-brick/30">NEW</span>
          </TabBtn>
        </div>

        {/* Body */}
        <div className="px-5 py-5 flex-1 overflow-y-auto">
          {tab === 'upload' && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => hiddenFileRef.current?.click()}
                className={[
                  'flex flex-col items-center justify-center text-center cursor-pointer',
                  'rounded-lg border-2 border-dashed transition-colors px-6 py-12 bg-white',
                  dragging ? 'border-brick bg-brick-tint/30' : 'border-brick/40 hover:border-brick hover:bg-brick-tint/20',
                ].join(' ')}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="text-brick mb-3">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <div className="text-sm font-semibold text-ink">Click to choose a file</div>
                <div className="text-xs text-ink-3 mt-1">or drag &amp; drop one here</div>
                <div className="text-[11px] text-ink-4 mt-3">PNG, JPG, or SVG up to 10 MB · 300 DPI recommended</div>
              </div>
              {/* The actual file input the parent listens on. Hidden — the
                  drag-drop zone above synthesises a `change` event onto it. */}
              <input ref={hiddenFileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { onPickFile(e); onClose() }} />
              <ul className="text-[11px] text-ink-3 space-y-0.5 leading-snug">
                <li>· Minimum <strong className="text-ink-2">300 DPI</strong> for sharp prints</li>
                <li>· Recommended <strong className="text-ink-2">1000 × 1000 px</strong> or larger</li>
                <li>· Transparent PNG works best on dark garments</li>
              </ul>
            </div>
          )}

          {tab === 'ai' && (
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">What should your design be?</label>
                <textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  placeholder='e.g. "a roaring lion silhouette with a crown", "vintage surf logo with the word EVRST", "minimalist mountain peak"'
                  disabled={genState === 'busy'}
                  className="input !text-sm !py-2 disabled:opacity-60" />
                <div className="text-[10px] text-ink-3 mt-1 flex justify-between">
                  <span>Logo-style, transparent background, print-ready.</span>
                  <span className="font-mono">{prompt.length} / 600</span>
                </div>
              </div>

              {/* ── Optional reference image ──────────────────────── */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">
                  Reference image <span className="text-ink-4 normal-case tracking-normal font-normal">— optional, used for style only</span>
                </label>
                <input ref={refInputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) pickReference(f); e.target.value = '' }} />
                {refDataUrl ? (
                  <div className="flex items-center gap-3 rounded border border-brick/30 bg-white p-2">
                    <img src={refDataUrl} alt="" className="w-14 h-14 object-cover rounded border border-border" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-ink font-semibold truncate">{refName}</div>
                      <div className="text-[10px] text-ink-3">Used as a style / composition reference — not traced.</div>
                    </div>
                    <button type="button" onClick={() => { setRefDataUrl(null); setRefName(null) }} disabled={genState === 'busy'}
                      className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 hover:text-red-600 px-2 py-1 rounded">Remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => refInputRef.current?.click()} disabled={genState === 'busy'}
                    className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded border border-dashed border-brick/40 bg-white text-sm text-ink-2 hover:border-brick hover:text-brick disabled:opacity-50">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    Upload an image for inspiration
                  </button>
                )}
              </div>

              {/* ── Optional brand palette pin ────────────────────── */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">
                  Brand colours <span className="text-ink-4 normal-case tracking-normal font-normal">— optional; if set, override any colours in your brief</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <ColourSwatch
                    label="Primary"
                    hex={primaryHex}
                    onChange={setPrimaryHex}
                    disabled={genState === 'busy'} />
                  <ColourSwatch
                    label="Secondary"
                    hex={secondaryHex}
                    onChange={setSecondaryHex}
                    disabled={genState === 'busy'} />
                </div>
                {(primaryHex || secondaryHex) && (
                  <p className="text-[11px] text-brick-dark mt-2 font-semibold">
                    Pinned. The AI will use only {primaryHex && <span className="font-mono">{primaryHex}</span>}{primaryHex && secondaryHex && ' + '}{secondaryHex && <span className="font-mono">{secondaryHex}</span>}, ignoring any other colour in your brief.
                  </p>
                )}
              </div>

              <div className="rounded border border-brick/30 bg-brick-tint/40 px-3 py-2 text-[11px] text-ink-2 leading-relaxed">
                We'll automatically pick a colour palette that <strong className="text-ink">stands out against your {garmentColourName ?? 'garment'} colour</strong> and render the design at 300 DPI for sharp printing. The full-resolution file is saved with your order; the canvas just shows a small preview while you position it.
              </div>

              {genState === 'error' && genError && (
                <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-900 font-semibold">
                  {genError}
                </div>
              )}

              {/* Progress bar — only shown while a paid call is in flight.
                  Width is bound to the smooth time-based fake progress;
                  snaps to 100 % on completion just before the modal closes. */}
              {genState === 'busy' && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] font-semibold">
                    <span className="text-ink">{genStage}</span>
                    <span className="font-mono text-brick">{Math.round(genProgress)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-brick-tint overflow-hidden border border-brick/30">
                    <div
                      className="h-full bg-brick transition-[width] duration-200 ease-out"
                      style={{ width: `${genProgress}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-ink-3">
                    Two-stage render — generating the artwork, then removing the background for a fully transparent PNG.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button onClick={() => setTab('upload')} disabled={genState === 'busy'}
                  className="px-3 py-2 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint disabled:opacity-30">
                  Upload instead
                </button>
                <button onClick={generate} disabled={genState === 'busy' || !prompt.trim()}
                  className="flex-1 px-4 py-2 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {genState === 'busy' ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} className="animate-spin">
                        <circle cx="12" cy="12" r="10" strokeDasharray="50 30"/>
                      </svg>
                      Generating…
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l2 7h7l-5.5 4.5L17 21l-5-4-5 4 1.5-7.5L3 9h7l2-7z"/>
                      </svg>
                      Generate design
                    </>
                  )}
                </button>
              </div>

              <p className="text-[10px] text-ink-3 italic">
                Tip: be specific about subject, style, and mood — "geometric wolf head in single-line tattoo style" beats "wolf".
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Compact colour picker with a swatch preview + hex readout. A null
// hex renders as "Pick…"; clicking the swatch opens the native colour
// picker. Clearing returns to null.
function ColourSwatch({ label, hex, onChange, disabled }: {
  label: string
  hex: string | null
  onChange: (hex: string | null) => void
  disabled?: boolean
}) {
  // Pre-populate the native picker with a middle-grey when no colour
  // is set yet so it opens to something neutral.
  const value = hex ?? '#888888'
  return (
    <div className="flex items-center gap-2 rounded border border-border bg-white px-2 py-1.5">
      <label className="relative w-9 h-9 rounded border border-border-2 overflow-hidden cursor-pointer shrink-0">
        <input type="color" value={value} disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
        <span className="absolute inset-0" style={{ background: hex ?? 'transparent' }} />
        {!hex && (
          <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ink-3 font-bold uppercase tracking-wider bg-gradient-to-br from-white via-surface to-border">+</span>
        )}
      </label>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-ink-3">{label}</div>
        <div className="font-mono text-xs text-ink truncate">{hex ?? 'Pick a colour'}</div>
      </div>
      {hex && (
        <button type="button" onClick={() => onChange(null)} disabled={disabled}
          className="text-[11px] text-ink-3 hover:text-red-600 font-semibold"
          title="Clear">×</button>
      )}
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={[
        'flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-bold rounded-t border-b-2 -mb-px',
        active ? 'border-brick text-brick bg-cream' : 'border-transparent text-ink-3 hover:text-brick hover:border-brick/40',
      ].join(' ')}>
      {children}
    </button>
  )
}
